import mustache from "mustache";
import * as turf from "@turf/turf";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { cultivoGrpcCatalogService } from "../../../../../modulos/catalogos/aplicaciones/services/CultivoGrpcCatalogService";
import { loteNoProductivoGrpcCatalogService } from "../../../../../modulos/catalogos/aplicaciones/services/LoteNoProductivoGrpcCatalogService";
import { CultivoItem } from "../../../../../generated/grpc/catalogos_aplicaciones_pb";
import { ClienteFincaResponse } from "../../../../../modulos/terceros/response/ClienteFincaResponse";
import {
  RegistroFincaBorrador,
  RegistroFincaBorradorLote,
} from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";
import {
  parseWizardError,
  parseWizardErrorMessage,
} from "../../../../../modulos/terceros/wizard/response/WizardValidationErrorResponse";
import {
  ActualizacionLoteTemp,
  registroFincaTempGrpcService,
} from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import { IConnectRender } from "../../../../definitions/IConnectRender";
import { loteAreaHectareas } from "../map/LoteSplitState";
import {
  BaseRegistroFincaPasoRender,
  RegistroFincaFooterOptions,
} from "./BaseRegistroFincaPasoRender";
import rowsTemplate from "./registroFincaPaso6RowsTemplate.html";
import template from "./registroFincaPaso6Template.html";

mustache.parse(template);
mustache.parse(rowsTemplate);

/** Paso 6 del wizard de registro: resumen final antes de crear la finca de
 *  verdad.
 *
 *  **Los datos salen del registro temporal del servidor.** Al entrar se lee
 *  el registro (`leerAsync`) y se pinta con lo que el servidor tiene: es
 *  exactamente lo que el guardado final va a volcar, así que el resumen no
 *  puede mostrar algo distinto de lo que se guarda. Hasta que llega la
 *  respuesta, la tabla queda vacía.
 *
 *  **Editable** (nombre de la finca, nombre, área productiva y cultivo o
 *  cobertura por lote) — última oportunidad de corregir sin volver pasos
 *  atrás. Cada cambio se guarda en el registro temporal en el momento, en
 *  orden (`_encolar`), y actualiza la copia de pantalla (`_lotes`). La columna "Cultivo /
 *  cobertura" se muestra siempre: cultivos si el lote es productivo,
 *  coberturas si no. Todas las stats salen de `_calcularStats`.
 *
 *  **"Guardar"** espera los cambios pendientes y llama a `Finalizar`: el
 *  servidor lee el registro temporal, exige que esté completo
 *  (`REGISTRO_INCOMPLETO` si no, con lo que falta) y crea la finca —o
 *  completa la existente, en el modo completar— en una sola transacción. En
 *  éxito notifica `_onGuardado`; `RegistroFincaModalRender` lo propaga y
 *  cierra el modal.
 *
 *  **En error**, el toast se muestra siempre (`parseWizardErrorMessage`), y
 *  según el `code` (`parseWizardError`) puede mandar al usuario a corregir
 *  donde se puede: `GEOMETRIA_SOLAPADA`/`GEOMETRIA_INVALIDA` → paso 3,
 *  `LOTE_SOLAPADO` → paso 4, `TIPO_INVALIDO` → paso 1. El resto
 *  (`NOMBRE_DUPLICADO`, `LOTE_NOMBRE_DUPLICADO`, `REGISTRO_INCOMPLETO`) se
 *  queda en paso 6: los nombres son editables acá mismo, y el mensaje de
 *  incompleto ya dice qué paso falta. */
export class RegistroFincaPaso6Render extends BaseRegistroFincaPasoRender {
  private _cultivos: CultivoItem[] = [];
  /** Coberturas de los lotes NO productivos — mismo catálogo que ofrece la
   *  barra del paso 5. */
  private _coberturas: { id: string; nombre: string }[] = [];
  private _tableBodyEl: HTMLElement | null = null;
  private _nombreInputEl: HTMLInputElement | null = null;
  private _statTotalEl: HTMLElement | null = null;
  private _statProductivosEl: HTMLElement | null = null;
  private _statCultivosEl: HTMLElement | null = null;
  private _statAreaEl: HTMLElement | null = null;
  /** Guardados de las ediciones, encadenados para que lleguen en orden. */
  private _colaGuardado: Promise<void> = Promise.resolve();
  /** Lotes tal como los leyó el servidor, con las ediciones de esta
   *  pantalla (que se guardan al momento). */
  private _lotes: RegistroFincaBorradorLote[] = [];
  private _disposed = false;

  constructor(
    private readonly _borrador: RegistroFincaBorrador,
    private readonly _onVolver: () => void,
    private readonly _onGuardado: (finca: ClienteFincaResponse) => void,
    private readonly _onIrACorregir: (paso: 1 | 3 | 4) => void,
  ) {
    super(_borrador.clienteId);
  }

  renderBody(): string {
    return mustache.render(template, {
      nombre: this._borrador.nombre ?? "",
      tipoLabel:
        this._borrador.tipo === 0
          ? "Agricultura"
          : this._borrador.tipo === 1
            ? "Ganadería"
            : "—",
      ...this._calcularStats(),
    });
  }

  /** Las cuatro stats del resumen, calculadas en UN solo lugar: las usan el
   *  primer pintado (`renderBody`) y cada refresco (`_refreshStats`).
   *
   *  - **Cultivos registrados** cuenta solo los cultivos de lotes
   *    PRODUCTIVOS (la cobertura de los no productivos va aparte, en
   *    `coberturaId`).
   *  - **Área total** se suma lote por lote, cada uno con su propio `try`:
   *    un lote con geometría ilegible no puede dejar el total entero en 0. */
  private _calcularStats(): {
    totalLotes: number;
    lotesProductivos: number;
    cultivosDistintos: number;
    areaTotalHa: string;
  } {
    const lotes = this._lotes;
    const productivos = lotes.filter((l) => l.isAreaProductiva === true);

    const areaTotalM2 = lotes.reduce((total, lote) => {
      try {
        const area = turf.area(lote.areaGeograficaPolygon);
        return Number.isFinite(area) ? total + area : total;
      } catch {
        return total;
      }
    }, 0);

    return {
      totalLotes: lotes.length,
      lotesProductivos: productivos.length,
      cultivosDistintos: new Set(
        productivos.map((l) => l.cultivoId).filter((id): id is string => !!id),
      ).size,
      areaTotalHa: (areaTotalM2 / 10000).toFixed(2),
    };
  }

  connectBody(): void {
    this._tableBodyEl = document.getElementById("registroFincaPaso6TableBody");
    this._nombreInputEl = document.getElementById(
      "registroFincaPaso6NombreInput",
    ) as HTMLInputElement | null;
    this._statTotalEl = document.getElementById("registroFincaPaso6StatTotal");
    this._statProductivosEl = document.getElementById(
      "registroFincaPaso6StatProductivos",
    );
    this._statCultivosEl = document.getElementById(
      "registroFincaPaso6StatCultivos",
    );
    this._statAreaEl = document.getElementById("registroFincaPaso6StatArea");

    this._nombreInputEl?.addEventListener("change", this._onNombreFincaChange);

    if (!this._tableBodyEl) return;

    // Los DOS catálogos: cada fila usa el que corresponde a su tipo de lote.
    cultivoGrpcCatalogService.getListAsync().then((cultivos) => {
      this._cultivos = cultivos;
      this._refreshRows();
    });
    loteNoProductivoGrpcCatalogService.getListAsync().then((coberturas) => {
      this._coberturas = coberturas;
      this._refreshRows();
    });

    this._cargarDesdeServidor();
  }

  disposeBody(): void {
    this._disposed = true;
    this._nombreInputEl?.removeEventListener("change", this._onNombreFincaChange);
  }

  getFooterOptions(): RegistroFincaFooterOptions {
    return {
      volver: { onClick: this._onVolver },
      submit: { label: "Guardar", onClick: this._onSubmitClick },
    };
  }

  getInstructions(): IConnectRender | null {
    return null;
  }

  /** Relee el registro temporal y repinta con lo que tiene el servidor. */
  private _cargarDesdeServidor(): void {
    registroFincaTempGrpcService
      .leerAsync(this._borrador)
      .then((leido) => {
        if (this._disposed) return;

        this._borrador.nombre = leido.borrador.nombre;
        this._borrador.tipo = leido.borrador.tipo;
        this._borrador.ultimoPaso = leido.borrador.ultimoPaso;
        this._lotes = leido.lotes;

        if (this._nombreInputEl) {
          this._nombreInputEl.value = leido.borrador.nombre ?? "";
        }
        this._refreshRows();
      })
      .catch((error) => {
        serviceProvider
          .getErrorService()
          .addError(
            "Error al cargar el registro de la finca",
            parseWizardErrorMessage(error),
          );
      });
  }

  private _onNombreFincaChange = (e: Event): void => {
    const input = e.target as HTMLInputElement;
    input.value = input.value.toUpperCase();
    this._borrador.nombre = input.value;
    // Mismo rpc del paso 1: valida que el nombre no esté repetido.
    this._encolar(async () => {
      await registroFincaTempGrpcService.guardarDatosBasicosAsync(this._borrador);
    }, "Error al guardar el nombre");
  };

  /** Encadena un guardado detrás de los anteriores; un error se avisa y no
   *  corta la cadena. */
  private _encolar(guardado: () => Promise<void>, titulo: string): void {
    this._colaGuardado = this._colaGuardado.then(guardado).catch((error) => {
      serviceProvider
        .getErrorService()
        .addError(titulo, parseWizardErrorMessage(error));
    });
  }

  private _guardarLote(actualizacion: ActualizacionLoteTemp): void {
    const registroId = this._borrador.registroId;
    if (!registroId) return;
    this._encolar(
      () =>
        registroFincaTempGrpcService.actualizarLotesAsync(registroId, [
          actualizacion,
        ]),
      "Error al guardar el lote",
    );
  }

  private _onSubmitClick = (): void => {
    const registroId = this._borrador.registroId;
    if (!registroId) return;

    const btnSubmit = document.getElementById(
      "registroFincaSubmit",
    ) as HTMLButtonElement | null;
    if (btnSubmit) btnSubmit.disabled = true;

    // Primero las ediciones pendientes: el servidor vuelca lo que tiene el
    // registro temporal, no lo que se ve en pantalla.
    this._colaGuardado
      .then(() => registroFincaTempGrpcService.finalizarAsync(registroId))
      .then((finca) =>
        this._onGuardado(finca as unknown as ClienteFincaResponse),
      )
      .catch((error) => {
        const parsed = parseWizardError(error);
        serviceProvider
          .getErrorService()
          .addError("Error al guardar la finca", parsed?.message ?? parseWizardErrorMessage(error));

        // Geometría/lotes solapados no se pueden corregir desde el resumen
        // — mandar al paso donde sí se puede. El resto (nombre duplicado,
        // nombre de lote duplicado, registro incompleto) se queda acá.
        switch (parsed?.code) {
          case "GEOMETRIA_SOLAPADA":
          case "GEOMETRIA_INVALIDA":
            this._onIrACorregir(3);
            return;
          case "LOTE_SOLAPADO":
            this._onIrACorregir(4);
            return;
          case "TIPO_INVALIDO":
            this._onIrACorregir(1);
            return;
        }

        if (btnSubmit) btnSubmit.disabled = false;
      });
  };

  private _refreshRows(): void {
    if (!this._tableBodyEl) return;

    const lotes = this._lotes;

    // El catálogo de cada fila lo decide el LOTE: cultivos si produce,
    // coberturas si no.
    //
    // **Salvo el lote PRODUCTIVO de una finca de ganadería** (2026-10-09): ahí
    // no hay catálogo, porque es un potrero y su cobertura es el pasto. La
    // columna muestra "Pasto" en vez del select. El lote NO productivo elige
    // cobertura como en cualquier finca: es como se asignan los bosques, las
    // construcciones y los cuerpos de agua, que una finca de ganadería tiene
    // igual. Mismo criterio que la barra del paso 5
    // (`LoteAssignBarRender._refreshCultivoVisibility`), y lo que el servidor
    // ya hacía por su cuenta: descarta el cultivo de una finca de ganadería al
    // guardarla (`PersistirFincaAsync`).
    const esGanaderia = this._borrador.tipo === 1;
    this._tableBodyEl.innerHTML = mustache.render(rowsTemplate, {
      lotes: lotes.map((lote, index) => {
        const productivo = lote.isAreaProductiva === true;
        const catalogo = productivo ? this._cultivos : this._coberturas;
        const elegido = productivo ? lote.cultivoId : lote.coberturaId;
        return {
          index,
          sinCultivo: esGanaderia && productivo,
          nombreLote: lote.nombreLote,
          areaHa: loteAreaHectareas(lote.areaGeograficaPolygon),
          sinAsignar: lote.isAreaProductiva === null,
          esSi: lote.isAreaProductiva === true,
          esNo: lote.isAreaProductiva === false,
          placeholderCultivo: productivo ? "(Sin cultivo)" : "(Sin cobertura)",
          cultivoOptions: catalogo.map((item) => ({
            id: item.id,
            nombre: item.nombre,
            selected: item.id === elegido,
          })),
        };
      }),
    });

    this._connectRowInputs();
    this._refreshStats();
  }

  private _connectRowInputs(): void {
    const loteEn = (index: number): RegistroFincaBorradorLote | undefined =>
      this._lotes[index];

    this._tableBodyEl
      ?.querySelectorAll<HTMLInputElement>(".registro-finca-resumen-name-input")
      .forEach((input) => {
        const index = Number(input.dataset.index);
        input.addEventListener("change", () => {
          // El nombre del LOTE se guarda tal como se escribe (2026-10-09): acá
          // había un `toUpperCase()`, igual que en el paso 4, y entre los dos
          // hacían que el mismo lote quedara "Lote 71" o "LOTE 71" según dónde
          // se lo tocara. El del nombre de la FINCA sí se conserva
          // (`_onNombreFincaChange`): esa convención no cambió.
          const lote = loteEn(index);
          if (!lote) return;
          lote.nombreLote = input.value;
          if (lote.id) {
            this._guardarLote({ id: lote.id, nombreLote: lote.nombreLote });
          }
        });
      });

    this._tableBodyEl
      ?.querySelectorAll<HTMLSelectElement>(
        ".registro-finca-resumen-productiva-select",
      )
      .forEach((select) => {
        const index = Number(select.dataset.index);
        select.addEventListener("change", () => {
          const lote = loteEn(index);
          if (!lote) return;
          lote.isAreaProductiva =
            select.value === "si" ? true : select.value === "no" ? false : null;
          // Cambiar de productivo a no productivo (o al revés) cambia de
          // CATÁLOGO: lo elegido en el otro ya no aplica.
          lote.cultivoId = null;
          lote.coberturaId = null;
          if (lote.id) {
            this._guardarLote({
              id: lote.id,
              asignacion: {
                isAreaProductiva: lote.isAreaProductiva,
                cultivoId: null,
                coberturaId: null,
              },
            });
          }
          this._refreshRows();
        });
      });

    this._tableBodyEl
      ?.querySelectorAll<HTMLSelectElement>(".registro-finca-resumen-cultivo-select")
      .forEach((select) => {
        const index = Number(select.dataset.index);
        select.addEventListener("change", () => {
          const lote = loteEn(index);
          if (!lote) return;
          const elegido = select.value || null;
          if (lote.isAreaProductiva) {
            lote.cultivoId = elegido;
          } else {
            lote.coberturaId = elegido;
          }
          if (lote.id) {
            this._guardarLote({
              id: lote.id,
              asignacion: {
                isAreaProductiva: lote.isAreaProductiva,
                cultivoId: lote.cultivoId,
                coberturaId: lote.coberturaId,
              },
            });
          }
          this._refreshStats();
        });
      });
  }

  private _refreshStats(): void {
    const stats = this._calcularStats();

    if (this._statTotalEl) {
      this._statTotalEl.textContent = String(stats.totalLotes);
    }
    if (this._statProductivosEl) {
      this._statProductivosEl.textContent = `${stats.lotesProductivos} de ${stats.totalLotes}`;
    }
    if (this._statCultivosEl) {
      this._statCultivosEl.textContent = String(stats.cultivosDistintos);
    }
    if (this._statAreaEl) {
      this._statAreaEl.textContent = `${stats.areaTotalHa} ha`;
    }
  }
}
